import { useState, useEffect, useRef, useCallback } from 'react';
import { Spin, Empty, Card, Collapse } from 'antd';
import { CheckCircleFilled, ThunderboltOutlined } from '@ant-design/icons';
import DetailPreferenceToggle from '../utils/DetailPreferenceToggle';
import { catchError } from '../../services/DaoService';
import { DashboardService } from '../../services/DashboardService';
import AffaireBacklogItem from '../affaire/AffaireBacklogItem';
import TacheBacklogItem from '../tache/TacheBacklogItem';
import TacheQuickView from '../tache/TacheQuickView';
import AffaireQuickView from '../affaire/AffaireQuickView';
import { useDetailPreference } from '../../hooks/useDetailPreference';
import PropTypes from 'prop-types';

function FeedList({ filters, onStats, onAssignes, onTotal, onTacheClick, onAffaireClick, onItemUpdate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [meta, setMeta] = useState(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!onItemUpdate) return;
    onItemUpdate.current = (updated, type) => {
      const isResolved = type === 'tache' ? updated.statut?.resolu : updated.done;
      setItems(prev =>
        isResolved
          ? prev.filter(row => row.item?.uid !== updated.uid)
          : prev.map(row => row.item?.uid === updated.uid ? { ...row, item: updated } : row)
      );
    };
  });

  const load = useCallback((page = 1, append = false) => {
    if (page === 1) setLoading(true); else setLoadingMore(true);
    DashboardService.getInstance()
      .getFeed(page, { ...filters, is_closed: false })
      .then(res => {
        const data = res.data;
        setMeta(data);
        setItems(prev => append ? [...prev, ...data.data] : data.data);
        if (data.stats    && onStats)    onStats(data.stats);
        if (data.assignes && onAssignes) onAssignes(data.assignes);
        if (page === 1 && onTotal) onTotal(data.total);
      })
      .catch(catchError)
      .finally(() => { setLoading(false); setLoadingMore(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  useEffect(() => { load(1); }, [load]);

  const loadMore = useCallback(() => {
    if (!meta?.next_page_url || loadingMore) return;
    load(meta.current_page + 1, true);
  }, [meta, loadingMore, load]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <Spin spinning={loading && items.length === 0}>
      {items.length === 0 && !loading
        ? <div style={{ padding: '40px 0' }}><Empty description="Aucun élément à afficher" /></div>
        : (
          <>
            {items.map((row, i) => (
              <div
                key={`${row.type}-${row.item?.uid ?? row.item?.id}-${i}`}
                style={{ borderBottom: '1px solid var(--border-subtle, #F1F2F4)' }}
              >
                {row.type === 'affaire'
                  ? <AffaireBacklogItem affaire={row.item} onItemClick={onAffaireClick} onUpdate={(updated) => onItemUpdate?.current?.(updated, 'affaire')} />
                  : <TacheBacklogItem tache={row.item} showAffaire onItemClick={onTacheClick} onUpdate={(updated) => onItemUpdate?.current?.(updated, 'tache')} />
                }
              </div>
            ))}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {loadingMore && (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <Spin size="small" />
              </div>
            )}
            {!loadingMore && !meta?.next_page_url && items.length > 0 && (
              <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 12, color: 'var(--text-disabled, #8C9BAB)' }}>
                — {items.length} élément{items.length !== 1 ? 's' : ''} au total —
              </div>
            )}
          </>
        )
      }
    </Spin>
  );
}

function DoneSection({ filters, onTacheClick, onAffaireClick, onItemUpdate }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [meta, setMeta] = useState(null);
  const [dirty, setDirty] = useState(false);
  const sentinelRef = useRef(null);
  const filtersRef = useRef(filters);

  useEffect(() => {
    if (!onItemUpdate) return;
    onItemUpdate.current = (updated, type) => {
      setItems(prev => prev.map(row => row.item?.uid === updated.uid ? { ...row, item: updated } : row));
    };
  });

  useEffect(() => { filtersRef.current = filters; });

  const load = useCallback((page = 1, append = false) => {
    if (page === 1) setLoading(true); else setLoadingMore(true);
    DashboardService.getInstance()
      .getFeed(page, { ...filtersRef.current, is_closed: true })
      .then(res => {
        const data = res.data;
        setMeta(data);
        setItems(prev => append ? [...prev, ...data.data] : data.data);
        setDirty(false);
      })
      .catch(catchError)
      .finally(() => { setLoading(false); setLoadingMore(false); });
  }, []);

  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    if (open) load(1);
    else setDirty(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  const handleToggle = (keys) => {
    const isOpen = keys.includes('done');
    setOpen(isOpen);
    if (isOpen && (dirty || items.length === 0)) load(1);
  };

  const loadMore = useCallback(() => {
    if (!meta?.next_page_url || loadingMore) return;
    load(meta.current_page + 1, true);
  }, [meta, loadingMore, load]);

  useEffect(() => {
    if (!open) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, open]);

  const total = meta?.total ?? null;
  const label = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, fontSize: 13 }}>
      <CheckCircleFilled style={{ color: '#52C41A' }} />
      Terminés
      {total !== null && (
        <span style={{ fontSize: 12, color: '#8C9BAB', fontWeight: 400 }}>({total})</span>
      )}
    </span>
  );

  return (
    <Collapse
      onChange={handleToggle}
      ghost={false}
      style={{ borderTop: '1px solid var(--border-subtle, #F1F2F4)', borderRadius: 0 }}
      items={[{
        key: 'done',
        label,
        children: (
          <Spin spinning={loading && items.length === 0}>
            {items.length === 0 && !loading
              ? <div style={{ padding: '24px 0' }}><Empty description="Aucun élément terminé" /></div>
              : (
                <>
                  {items.map((row, i) => (
                    <div
                      key={`${row.type}-${row.item?.uid ?? row.item?.id}-${i}`}
                      style={{ borderBottom: '1px solid var(--border-subtle, #F1F2F4)' }}
                    >
                      {row.type === 'affaire'
                        ? <AffaireBacklogItem affaire={row.item} onItemClick={onAffaireClick} onUpdate={(updated) => onItemUpdate?.current?.(updated, 'affaire')} />
                        : <TacheBacklogItem tache={row.item} showAffaire onItemClick={onTacheClick} onUpdate={(updated) => onItemUpdate?.current?.(updated, 'tache')} />
                      }
                    </div>
                  ))}
                  <div ref={sentinelRef} style={{ height: 1 }} />
                  {loadingMore && (
                    <div style={{ textAlign: 'center', padding: '12px 0' }}>
                      <Spin size="small" />
                    </div>
                  )}
                </>
              )
            }
          </Spin>
        ),
      }]}
    />
  );
}

export default function MainFeed({ filters = {}, onStats, onAssignes }) {
  const [activeTotal, setActiveTotal] = useState(null);
  const [selectedTacheUid, setSelectedTacheUid] = useState(null);
  const [selectedAffaireUid, setSelectedAffaireUid] = useState(null);
  const { mode, changeMode } = useDetailPreference();

  const feedListUpdater = useRef(null);
  const doneSectionUpdater = useRef(null);

  const handleTacheUpdate = (updated) => {
    if (!updated) return;
    feedListUpdater.current?.(updated, 'tache');
    doneSectionUpdater.current?.(updated, 'tache');
  };

  const handleAffaireUpdate = (updated) => {
    if (!updated) return;
    feedListUpdater.current?.(updated, 'affaire');
    doneSectionUpdater.current?.(updated, 'affaire');
  };

  return (
    <>
      <DetailPreferenceToggle mode={mode} onChange={changeMode} />

      <Card styles={{ body: { padding: 0 } }}>
        <Collapse
          defaultActiveKey={['active']}
          ghost={false}
          style={{ borderRadius: 0 }}
          items={[{
            key: 'active',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, fontSize: 13 }}>
                <ThunderboltOutlined style={{ color: 'var(--primary-color, #3E65AC)' }} />
                Ouverts
                {activeTotal != null && (
                  <span style={{ fontSize: 12, color: '#8C9BAB', fontWeight: 400 }}>({activeTotal})</span>
                )}
              </span>
            ),
            children: (
              <FeedList
                filters={filters}
                onStats={onStats}
                onAssignes={onAssignes}
                onTotal={setActiveTotal}
                onTacheClick={setSelectedTacheUid}
                onAffaireClick={setSelectedAffaireUid}
                onItemUpdate={feedListUpdater}
              />
            ),
          }]}
        />
        <DoneSection
          filters={filters}
          onTacheClick={setSelectedTacheUid}
          onAffaireClick={setSelectedAffaireUid}
          onItemUpdate={doneSectionUpdater}
        />
      </Card>

      <TacheQuickView
        uid={selectedTacheUid}
        mode={mode}
        onClose={() => setSelectedTacheUid(null)}
        onUpdate={handleTacheUpdate}
      />
      <AffaireQuickView
        uid={selectedAffaireUid}
        mode={mode}
        onClose={() => setSelectedAffaireUid(null)}
        onUpdate={handleAffaireUpdate}
      />
    </>
  );
}

MainFeed.propTypes = {
  filters:    PropTypes.object,
  onStats:    PropTypes.func,
  onAssignes: PropTypes.func,
};

FeedList.propTypes = {
  filters:        PropTypes.object,
  onStats:        PropTypes.func,
  onAssignes:     PropTypes.func,
  onTotal:        PropTypes.func,
  onTacheClick:   PropTypes.func,
  onAffaireClick: PropTypes.func,
  onItemUpdate:   PropTypes.object,
};

DoneSection.propTypes = {
  filters:        PropTypes.object,
  onTacheClick:   PropTypes.func,
  onAffaireClick: PropTypes.func,
  onItemUpdate:   PropTypes.object,
};